<template>
  <div class="eye-canvas">
    <div id="eyeNodeContainer" ref="eyeNodeContainer" style="width: 500px; height: 400px;"></div>
  </div>
</template>

<script>
import Konva from 'konva';

export default {
  name: 'EyeKonva',
  data() {
    return {
      stage: null,
      layer: null,
      mainNodesLayer: null,
      subNodesLayer: null,
      connectionsLayer: null,
      popoverLayer: null, // 新增弹窗图层
      popoverGroup: null  // 当前弹窗Group
    };
  },
  mounted() {
    this.initKonva();
  },
  methods: {
    initKonva() {
      // 初始化Konva舞台
      this.stage = new Konva.Stage({
        container: 'eyeNodeContainer',
        width: 500,
        height: 400
      });

      // 创建不同的图层以便管理绘制顺序
      this.connectionsLayer = new Konva.Layer();
      this.mainNodesLayer = new Konva.Layer();
      this.subNodesLayer = new Konva.Layer();
      this.popoverLayer = new Konva.Layer();
      this.stage.add(this.connectionsLayer);
      this.stage.add(this.mainNodesLayer);
      this.stage.add(this.subNodesLayer);
      this.stage.add(this.popoverLayer);
    },

    drawNode(scores) {
      this.drawEyeDiagrams(scores);
    },

    _getColorByScore(score) {
      if (score === null || score === undefined) {
        return 'hsl(45, 90%, 60%)';
      }
      const lightness = 35 + Math.abs(score - 6) * 5;
      if (score >= 6) {
        return `hsl(120, 70%, ${lightness}%)`;
      } else {
        return `hsl(0, 70%, ${lightness}%)`;
      }
    },
    _createMainNode(node, text, size, desiredNodeCanvasHeight) {
      const group = new Konva.Group({
        x: node.x,
        y: node.y
      });

      // 创建六边形
      const hexagon = () => {
        const points = [];
        for (let i = 0; i < 6; i++) {
          points.push(
            size * Math.cos(Math.PI / 3 * i + Math.PI / 6),
            size * Math.sin(Math.PI / 3 * i + Math.PI / 6)
          );
        }
        return points;
      };

      // 主六边形线性渐变色，边框改为黑色
      const mainHexagon = new Konva.Line({
        points: hexagon(),
        closed: true,
        fillLinearGradientStartPoint: { x: -size, y: -size },
        fillLinearGradientEndPoint: { x: size, y: size },
        fillLinearGradientColorStops: [0, '#fff', 0.5, this._getColorByScore(node.score), 1, '#e0e0e0'],
        shadowColor: 'rgba(0,0,0,0.3)',
        shadowBlur: 12,
        shadowOffsetX: 6,
        shadowOffsetY: 6,
        shadowOpacity: 0.5
      });

      // 添加文字标签
      const textLabel = new Konva.Text({
        text: text,
        fontSize: desiredNodeCanvasHeight * 0.035,
        fontFamily: 'Microsoft YaHei',
        align: 'center',
        verticalAlign: 'middle',
        fill: '#000',
        y: size * 1.3,
        width: 200,
        offsetX: 100,
      });

      // 添加分数文本，并将位置调整到节点中心
      const score = node.score === null || node.score === undefined ? 'N/A' : node.score.toFixed(1);
      const scoreText = new Konva.Text({
        text: score,
        fontSize: desiredNodeCanvasHeight * 0.05,
        fontFamily: 'Microsoft YaHei',
        fontStyle: '700',
        fill: '#000',
        align: 'center',
        verticalAlign: 'middle',
        width: 80,
        offsetX: 40
      });
      scoreText.offsetY(scoreText.height() / 2);

      group.add(mainHexagon);
      group.add(textLabel);
      group.add(scoreText);

      scoreText.on('mouseover', (evt) => {
        const pos = evt.target.getAbsolutePosition();
        this._createPopover(pos.x, pos.y, text);
      });
      scoreText.on('mouseout', () => {
        this._removePopover();
      });
      return group;
    },

    _createSubNode(node, text, size, desiredNodeCanvasHeight) {
      const group = new Konva.Group({
        x: node.x,
        y: node.y
      });

      // 主圆径向渐变色，边框改为黑色
      const mainCircle = new Konva.Circle({
        radius: size,
        fillRadialGradientStartPoint: { x: -size * 0.5, y: -size * 0.5 },
        fillRadialGradientEndPoint: { x: 0, y: 0 },
        fillRadialGradientStartRadius: 0,
        fillRadialGradientEndRadius: size * 2,
        fillRadialGradientColorStops: [0, '#fff', 0.5, this._getColorByScore(node.score), 1, '#e0e0e0'],
        shadowColor: 'rgba(0,0,0,0.25)',
        shadowBlur: 8,
        shadowOffsetX: 4,
        shadowOffsetY: 4,
        shadowOpacity: 0.6
      });

      // 添加文字标签
      const textLabel = new Konva.Text({
        text: text,
        fontSize: desiredNodeCanvasHeight * 0.035,
        fontFamily: 'Microsoft YaHei',
        fontStyle: '400',
        align: 'center',
        verticalAlign: 'middle',
        fill: '#000',
        y: size * 1.2,
        width: 200,
        offsetX: 100,
      });

      // 添加分数文本，并将位置调整到节点中心
      const score = node.score === null || node.score === undefined ? 'N/A' : node.score.toFixed(1);
      const scoreText = new Konva.Text({
        text: score,
        fontSize: desiredNodeCanvasHeight * 0.04,
        fontFamily: 'Microsoft YaHei',
        fontStyle: 'bold',
        fill: '#000',
        align: 'center',
        verticalAlign: 'middle',
        width: 60,
        offsetX: 30
      });
      scoreText.offsetY(scoreText.height() / 2);

      group.add(mainCircle);
      group.add(textLabel);
      group.add(scoreText);

      scoreText.on('mouseover', (evt) => {
        const pos = evt.target.getAbsolutePosition();
        this._createPopover(pos.x, pos.y, text);
      });
      scoreText.on('mouseout', () => {
        this._removePopover();
      });
      return group;
    },

    _createPopover(x, y, text) {
      // 移除旧的popover
      if (this.popoverGroup) {
        this.popoverGroup.destroy();
        this.popoverGroup = null;
      }
      const padding = 10;
      const fontSize = 16;
      const fontFamily = 'Microsoft YaHei';
      const tempText = new Konva.Text({
        text,
        fontSize,
        fontFamily,
        fill: '#222',
        padding,
      });
      this.popoverLayer.add(tempText);
      const width = tempText.width() + padding * 2;
      const height = tempText.height() + padding * 2;
      tempText.destroy();

      // 获取画布宽高
      const container = this.stage.container();
      const canvasWidth = container.offsetWidth;
      const canvasHeight = container.offsetHeight;

      // 计算四个象限的弹窗位置
      const positions = [
        { x: x + 10, y: y + 10 }, // 右下
        { x: x + 10, y: y - height - 10 }, // 右上
        { x: x - width - 10, y: y + 10 }, // 左下
        { x: x - width - 10, y: y - height - 10 } // 左上
      ];
      // 选择不会超出边界的第一个位置
      let pos = positions[0];
      for (let p of positions) {
        if (
          p.x >= 0 &&
          p.x + width <= canvasWidth &&
          p.y >= 0 &&
          p.y + height <= canvasHeight
        ) {
          pos = p;
          break;
        }
      }

      const group = new Konva.Group({ x: pos.x, y: pos.y });
      const rect = new Konva.Rect({
        width,
        height,
        fill: '#fff',
        stroke: '#409EFF',
        strokeWidth: 1.5,
        cornerRadius: 8,
        shadowColor: '#aaa',
        shadowBlur: 8,
        shadowOffset: { x: 2, y: 2 },
        shadowOpacity: 0.2
      });
      const label = new Konva.Text({
        text,
        fontSize,
        fontFamily,
        fill: '#222',
        x: padding,
        y: padding,
        width: width - padding * 2,
        align: 'center',
        verticalAlign: 'middle',
      });
      group.add(rect);
      group.add(label);
      this.popoverLayer.add(group);
      this.popoverLayer.batchDraw();
      this.popoverGroup = group;
    },
    _removePopover() {
      if (this.popoverGroup) {
        this.popoverGroup.destroy();
        this.popoverGroup = null;
        this.popoverLayer.batchDraw();
      }
    },

    drawEyeDiagrams(lastQueryScores) {
      // 清空所有图层
      this.mainNodesLayer.destroyChildren();
      this.subNodesLayer.destroyChildren();
      this.connectionsLayer.destroyChildren();

      const container = this.stage.container();
      const desiredNodeCanvasWidth = container.offsetWidth;
      const desiredNodeCanvasHeight = container.offsetHeight;
      
      const baseRadius = desiredNodeCanvasHeight * 0.28;
      const radiusRange = desiredNodeCanvasHeight * 0.1; 
      const angleRange = Math.PI / 7;

      const leftAngle = (5 * Math.PI / 4) + (Math.random() * angleRange - angleRange / 2);
      const rightAngle = (7 * Math.PI / 4) + (Math.random() * angleRange - angleRange / 2);
      const radius = baseRadius + (Math.random() * radiusRange - radiusRange / 2);

      const centerX = desiredNodeCanvasWidth / 2;
      const topY = desiredNodeCanvasHeight * 0.35;
      const bottomNodesY = desiredNodeCanvasHeight * 0.60;

      const leftX = centerX + Math.cos(leftAngle) * radius;
      const rightX = centerX + Math.cos(rightAngle) * radius;
      
      const nodesData = {
          main: [
              { id: '生理因素', x: centerX, y: topY, children: ['眼轴', '视功能AC/A', '平衡屈光度', '视功能调节幅度'], baseColor: 'rgba(255,152,0,0.7)' }, // 橙色，透明度0.7
              { id: '环境因素', x: leftX, y: bottomNodesY, children: ['户外活动时长', '室内读写光环境'], baseColor: 'rgba(156,39,176,0.7)' }, // 紫色，透明度0.7
              { id: '行为因素', x: rightX, y: bottomNodesY, children: ['电子设备时长', '近距离用眼', '睡眠时长', '矫正行为', '复查间隔'], baseColor: 'rgba(33,150,243,0.7)' } // 蓝色，透明度0.7
          ],
          sub: {}
      };
      
      const subNodeDistance = baseRadius * 0.85;

      // 计算子节点位置和分数
      nodesData.main.forEach(mainNode => {
          const childCount = mainNode.children.length;
          let startAngle, endAngle;

          if (mainNode.y < desiredNodeCanvasHeight * 0.5) {
              startAngle = -Math.PI * (1.0); 
              endAngle = -Math.PI * (0.); 
          } else if (mainNode.x < centerX) { 
              startAngle = Math.PI * 0.50; 
              endAngle = Math.PI * 0.90; 
          } else { 
              startAngle = -Math.PI * 0.15; 
              endAngle = Math.PI * 0.70; 
          }
          
          const angleStep = (childCount > 1) ? (endAngle - startAngle) / (childCount - 1) : 0;
          nodesData.sub[mainNode.id] = mainNode.children.map((child, index) => {
              const angle = (childCount > 1) ? startAngle + angleStep * index : (startAngle + endAngle) / 2;
              const distance = subNodeDistance + Math.random() * (subNodeDistance * 0.2);
              let score;
              if (mainNode.id === '环境因素') {
                  if (child === '室内读写光环境') {
                      score = lastQueryScores && lastQueryScores.outdoorLightingScore !== undefined ? lastQueryScores.outdoorLightingScore : Math.floor(Math.random() * 10) + 1;
                  } else if (child === '户外活动时长') {
                      score = lastQueryScores && lastQueryScores.outdoorScore !== undefined ? lastQueryScores.outdoorScore : Math.floor(Math.random() * 10) + 1;
                  } else {
                      score = Math.floor(Math.random() * 10) + 1;
                  }
              } else if (mainNode.id === '行为因素') {
                  if (child === '电子设备时长') {
                      score = lastQueryScores && lastQueryScores.deviceScore !== undefined ? lastQueryScores.deviceScore : Math.floor(Math.random() * 10) + 1;
                  } else if (child === '矫正行为') {
                      score = lastQueryScores && lastQueryScores.restScore !== undefined ? lastQueryScores.restScore : Math.floor(Math.random() * 10) + 1;
                  } else if (child === '近距离用眼') {
                      score = lastQueryScores && lastQueryScores.eyeCloseScore !== undefined ? lastQueryScores.eyeCloseScore : Math.floor(Math.random() * 10) + 1;
                  } else if (child === '睡眠时长') { 
                      score = lastQueryScores && lastQueryScores.sleepScore !== undefined ? lastQueryScores.sleepScore : Math.floor(Math.random() * 10) + 1; 
                  } else if (child === '复查间隔') {
                      score = lastQueryScores && lastQueryScores.reviewScore !== undefined ? lastQueryScores.reviewScore : Math.floor(Math.random() * 10) + 1;
                  } else {
                      score = Math.floor(Math.random() * 10) + 1;
                  }
              } else if (mainNode.id === '生理因素') {
                  if (child === '视功能AC/A') { 
                      score = lastQueryScores && lastQueryScores.eyeACAScore !== undefined ? lastQueryScores.eyeACAScore : 9.0; 
                  } else if (child === '眼轴') { 
                      score = lastQueryScores && lastQueryScores.eyeLengthScore !== undefined ? lastQueryScores.eyeLengthScore : 8.0; 
                  } else if (child === '平衡屈光度') { 
                      score = lastQueryScores && lastQueryScores.eyeBalanceScore !== undefined ? lastQueryScores.eyeBalanceScore : 8.0; 
                  } else if (child === '视功能调节幅度') { 
                      score = lastQueryScores && lastQueryScores.eyeAAScore !== undefined ? lastQueryScores.eyeAAScore : 8.0; 
                  } else { 
                      score = Math.floor(Math.random() * 10) + 1; 
                  }
              } else { 
                  score = Math.floor(Math.random() * 10) + 1; 
              }
              
              return {
                  id: child,
                  x: mainNode.x + Math.cos(angle) * distance,
                  y: mainNode.y + Math.sin(angle) * distance,
                  score: score, 
                  color: null, 
                  baseColor: mainNode.baseColor
              };
          });
      });

      // 计算主节点的平均分数
      nodesData.main = nodesData.main.map(node => {
          const childNodes = nodesData.sub[node.id];
          if (childNodes && childNodes.length > 0) {
              const validChildNodes = childNodes.filter(child => child.score !== null && child.score !== undefined);
              if (validChildNodes.length > 0) {
                  if (node.id === '环境因素') {
                      const scoreWeights = {
                          '户外活动时长': 0.6,
                          '室内读写光环境': 0.4
                      };
                      const weightedChildren = validChildNodes.filter(child => scoreWeights[child.id] !== undefined);
                      if (weightedChildren.length > 0) {
                          const totalWeight = weightedChildren.reduce((sum, child) => sum + scoreWeights[child.id], 0);
                          const weightedScore = weightedChildren.reduce((sum, child) => sum + child.score * scoreWeights[child.id], 0);
                          return { ...node, score: Number((weightedScore / totalWeight).toFixed(1)), color: null };
                      }
                  }
                  const avgScore = validChildNodes.reduce((sum, child) => sum + child.score, 0) / validChildNodes.length;
                  return { ...node, score: Number(avgScore.toFixed(1)), color: null };
              } else {
                  return { ...node, score: null, color: null };
              }
          }
          return { ...node, score: node.score !== undefined ? node.score : 5, color: null };
      });
      
      // 绘制主节点之间的连线
      for (let i = 0; i < nodesData.main.length; i++) {
          for (let j = i + 1; j < nodesData.main.length; j++) {
              const line = new Konva.Line({
                  points: [
                      nodesData.main[i].x, nodesData.main[i].y,
                      nodesData.main[j].x, nodesData.main[j].y
                  ],
                  stroke: 'rgba(102, 102, 102, 0.25)',
                  strokeWidth: 3,
                  dash: [4, 4]
              });
              this.connectionsLayer.add(line);
          }
      }

      // 绘制主节点与子节点之间的连线
      nodesData.main.forEach(mainNode => {
          if (nodesData.sub[mainNode.id]) {
              nodesData.sub[mainNode.id].forEach(subNode => {
                  const line = new Konva.Line({
                      points: [
                          mainNode.x, mainNode.y,
                          subNode.x, subNode.y
                      ],
                      stroke: mainNode.baseColor,
                      strokeWidth: 3
                  });
                  this.connectionsLayer.add(line);
              });
          }
      });

      // 绘制主节点和子节点
      const mainNodeSize = desiredNodeCanvasHeight * 0.09;
      const subNodeSize = desiredNodeCanvasHeight * 0.06;

      // 添加主节点
      nodesData.main.forEach(node => {
          const mainNode = this._createMainNode(node, node.id, mainNodeSize, desiredNodeCanvasHeight);
          this.mainNodesLayer.add(mainNode);
      });

      // 添加子节点
      nodesData.main.forEach(mainNode => {
          if (nodesData.sub[mainNode.id]) {
              nodesData.sub[mainNode.id].forEach(subNode => {
                  const subNodeObj = this._createSubNode(subNode, subNode.id, subNodeSize, desiredNodeCanvasHeight);
                  this.subNodesLayer.add(subNodeObj);
              });
          }
      });

      // 绘制所有图层
      this.mainNodesLayer.draw();
      this.subNodesLayer.draw();
      this.connectionsLayer.draw();
    }
  }
}
</script>

<style scoped>
.eye-canvas {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  height: 100%;
}

#eyeNodeContainer {
  display: block;
  width: 500px;
  height: 400px;
  border: 1px solid #DCDFE6;
  border-radius: 20px;
  background-color: #fff;
  overflow: hidden;
}
</style>
